/**
 * chart-config.ts — single source of chart styling constants.
 *
 * Canonical rules (Design Language Codex §6 + §12):
 * - Light popover tooltip (bg-popover, hairline border, radius 10, shadow /0.08)
 * - Horizontal gridlines only, border color at 0.6 opacity
 * - Axis ticks: 11px, muted-foreground fill
 * - XAxis: preserveStartEnd interval, minTickGap 32, no tick/axis lines, dy 8
 * - Stroke weights: you = 2.5, other = 1.5
 * - Deterministic entity→color: you → series-1; others → series-2..6 by
 *   orderedEntityIds index (5 real competitor slots); true overflow beyond
 *   that (a 6th+ competitor) resolves to the separate `--series-rest` rollup
 *   — never a clamp onto the last real series color. See the brand-token-
 *   migration T10 series-count decision below `entityColor` for why.
 *
 * Callers must pass `orderedEntityIds` in the project's canonical ordering
 * (e.g. by competitor created_at) so that the same entity maps to the same
 * series-* variable on every chart of a page set.
 *
 * All consumers import from here — never inline tooltip/axis/grid styles per-chart.
 */

// ─── Tooltip ──────────────────────────────────────────────────────────────────

/**
 * Light popover tooltip content style — apply to the Recharts `contentStyle` prop.
 * round-4 audit (brand-token-migration): radius 12 (--radius-card, was a
 * stale 10) and shadow-overlay (the design source's "the one shadow" for
 * every floating/layered surface — tooltips included — was a raw hardcoded
 * box-shadow literal).
 */
export const CHART_TOOLTIP_STYLE = {
  borderRadius: 12,
  border: '1px solid var(--border)',
  backgroundColor: 'var(--popover)',
  fontSize: 12,
  padding: '8px 10px',
  boxShadow: 'var(--shadow-overlay)',
} as const

/**
 * Style applied to the label row inside the tooltip.
 */
export const CHART_TOOLTIP_LABEL_STYLE = {
  color: 'var(--muted-foreground)',
  marginBottom: 6,
  fontWeight: 500,
} as const

// ─── Grid ─────────────────────────────────────────────────────────────────────

/**
 * CartesianGrid props — horizontal lines only, border color at 0.6 opacity.
 * Spread directly onto <CartesianGrid {...CHART_GRID} />.
 */
export const CHART_GRID = {
  stroke: 'var(--border)',
  strokeOpacity: 0.6,
  vertical: false,
} as const

// ─── Axis ticks ───────────────────────────────────────────────────────────────

/**
 * Shared tick style for XAxis and YAxis — 11px, muted-foreground.
 * round-4 audit, corrected in review: the design source's literal spec is
 * "11px ink-350", but --ink-350 is theme-invariant (no .dark override) where
 * --muted-foreground carries a real dark-adapted value (--d-muted). Reverted
 * to --muted-foreground — every chart's axis ticks render in both themes,
 * unlike a static specimen.
 * Use as `tick={CHART_AXIS_TICK}`.
 */
export const CHART_AXIS_TICK = {
  fontSize: 11,
  fill: 'var(--muted-foreground)',
} as const

/**
 * Standard XAxis props (spread onto <XAxis>).
 * Codex §12: no tick/axis lines, dy 8, interval preserveStartEnd, minTickGap 32.
 */
export const CHART_XAXIS_PROPS = {
  tickLine: false,
  axisLine: false,
  tick: CHART_AXIS_TICK,
  dy: 8,
  interval: 'preserveStartEnd' as const,
  minTickGap: 32,
} as const

/**
 * Standard YAxis props (spread onto <YAxis>).
 * Callers may add tickFormatter, domain, ticks, width as needed.
 */
export const CHART_YAXIS_PROPS = {
  tickLine: false,
  axisLine: false,
  tick: CHART_AXIS_TICK,
} as const

/**
 * Y-axis margin/width pair for a chart whose left edge sits flush against its
 * container (a negative `margin.left` cancels Recharts' default axis gutter),
 * while still leaving enough room for the widest tick label this app renders
 * there — including decimal percentages on sub-1% domains ("0.02%", "<0.1%").
 *
 * The visible label budget is `width + CHART_YAXIS_FLUSH_LEFT_MARGIN_LEFT`
 * (the margin is negative, so this subtracts): the prior per-site literals —
 * e.g. `margin.left: -20` paired with `width: 40` — left only 20px visible,
 * clipping anything past 3 whole digits (growth-engine-r2e-craft E4).
 *
 * Opt-in, separate from `CHART_YAXIS_PROPS`: that object is spread by 6 charts
 * and only 5 of them want this geometry — bundling it in would silently
 * change the other 2 (`trend-competitor-section.tsx`, `visibility-history-
 * chart.tsx`), which are not part of this fix. Reference both constants only
 * at a call site's own `margin`/`width` props, alongside the unchanged
 * `{...CHART_YAXIS_PROPS}` spread:
 *
 * ```tsx
 * <LineChart margin={{ left: CHART_YAXIS_FLUSH_LEFT_MARGIN_LEFT, right: 4 }}>
 *   <YAxis {...CHART_YAXIS_PROPS} width={CHART_YAXIS_FLUSH_LEFT_WIDTH} ... />
 * ```
 */
export const CHART_YAXIS_FLUSH_LEFT_MARGIN_LEFT = -8

/** Pairs with `CHART_YAXIS_FLUSH_LEFT_MARGIN_LEFT` — see its docstring. */
export const CHART_YAXIS_FLUSH_LEFT_WIDTH = 48

// ─── Chart body height ────────────────────────────────────────────────────────

/**
 * `ChartContainer`'s standard body height for a full-width trend chart (DES-13) — shared by
 * `EngineSplitTrendChart`, the Visibility/Search trend, and `VisibilityHistoryChart`. A distinct
 * one-off (e.g. the SoV donut's 180px square, the sparkline detail sheet's 100px) records its own
 * value at the call site rather than pulling this constant in.
 */
export const CHART_BODY_HEIGHT_CLASS = 'h-[220px] w-full'

// ─── Stroke weights ───────────────────────────────────────────────────────────

/** Stroke weight for the "you" series (brand line). Codex §6: 2.5px. */
export const STROKE_YOU = 2.5

/** Stroke weight for competitor / other series. Codex §6: 1.5px. */
export const STROKE_OTHER = 1.5

// ─── Active dot configs ───────────────────────────────────────────────────────

/**
 * activeDot config for the "you" series.
 * r=4, strokeWidth=2, stroke=card background so it pops on hover.
 */
export const ACTIVE_DOT_YOU = {
  r: 4,
  strokeWidth: 2,
  stroke: 'var(--card)',
} as const

/**
 * activeDot config for competitor series.
 * Slightly smaller than ACTIVE_DOT_YOU.
 */
export const ACTIVE_DOT_OTHER = {
  r: 3.5,
  strokeWidth: 2,
  stroke: 'var(--card)',
} as const

// ─── Confidence band fill ───────────────────────────────────────────────────

/**
 * Fill opacity for a single-series confidence-band `Area` (Design Language
 * Codex §6: "Area fills: single-series only, 6–8% opacity of the line
 * color"). `WeeklyBandChart` is this constant's first consumer — never apply
 * it to a multi-series chart (the codex reserves area fills for the
 * single-series case; a second series sharing this opacity would read as a
 * second band, not a second line).
 */
export const CHART_BAND_FILL_OPACITY = 0.08

// ─── Competitor color palette (deprecated alias — derive from entityColor) ────

/**
 * Ordered CSS series-* variables for competitor series (indices 0–4 → series-2..6).
 *
 * @deprecated Use `entityColor()` instead for deterministic color assignment.
 * This constant is kept as a compatibility alias so existing consumers
 * (`SovChartLegend`, chart components) continue to work. The values are
 * intentionally derived from `entityColor()` so there is one source of truth.
 */
export const COMPETITOR_CHART_COLORS = [
  'var(--series-2)',
  'var(--series-3)',
  'var(--series-4)',
  'var(--series-5)',
  'var(--series-6)',
] as const

// ─── Deterministic entity color ───────────────────────────────────────────────

/**
 * Deterministic entity → CSS series-* color variable.
 *
 * brand-token-migration T10 series-count decision (DES-16 vs. the new design
 * source — see `docs/tasks/brand-token-migration/STATE.md`): the source
 * defines 6 real named series (`--series-1..6`) plus a *separate*
 * `--series-rest` rollup, not DES-16's old 5-named-plus-reserved-6th shape.
 * This resolver exposes all 6 named series (you + 5 competitors) and reserves
 * `--series-rest` strictly for genuine overflow past that — a 6th+
 * competitor — rather than clamping the 5th+ competitor onto the last named
 * color the way the pre-migration version of this function did (it clamped
 * onto the slot that has since become the reserved rollup, making that color
 * double as a real competitor color — exactly the ambiguity DES-16's own
 * `why:` warns against). DES-16's numeric ceiling should be updated to
 * reflect this via `/dev:retro`.
 *
 * Rule:
 * - `isYou: true` → always `var(--series-1)` (brand)
 * - Others → `var(--series-2)` through `var(--series-6)` by 0-based index in
 *   `orderedEntityIds` (index 0 = series-2, …, index 4 = series-6)
 * - Index 5+ (a 6th or later competitor) → `var(--series-rest)`, the
 *   dedicated "Others"/baseline rollup — never a 6th real series
 *
 * `orderedEntityIds` must come from the data layer's canonical competitor ordering
 * (e.g. by created_at ascending) so that the color assignment is stable across all
 * charts rendered in the same page set.
 *
 * @param entityId - The entity's stable identifier (competitor key, name, etc.)
 * @param orderedEntityIds - Ordered list of NON-YOU entity IDs (callers exclude "you")
 * @param opts.isYou - When true, returns series-1 regardless of position
 */
export function entityColor(
  entityId: string,
  orderedEntityIds: string[],
  opts?: { isYou?: boolean }
): string {
  if (opts?.isYou) return 'var(--series-1)'

  const index = orderedEntityIds.indexOf(entityId)
  const safeIndex = index < 0 ? 0 : index
  // series-2..6: 5 real competitor slots (index 0 → series-2, … index 4 → series-6)
  if (safeIndex < 5) return `var(--series-${safeIndex + 2})`
  // True overflow (a 6th+ competitor) — the dedicated rollup, not a clamp
  return 'var(--series-rest)'
}
